import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Eye, Brain, Target, Archive, ArrowRight, CheckCircle, Lightbulb, Users, Zap } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import { Helmet } from 'react-helmet-async';
import { Card } from './ui/Button';
import { CONTACT_DETAILS } from '../config/contactDetails';

export const MethodologyPage: React.FC = () => {
    const location = useLocation();
    const { trackPageView, trackClick, isSessionReady } = useEventTracking();

    useEffect(() => {
        if (isSessionReady) {
            trackPageView('/services');
        }
    }, [isSessionReady]);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }, 100);
            }
        }
    }, [location.hash]);

    const handleCTAClick = (ctaType: string) => {
        trackClick(`services-cta-${ctaType}`, `Services ${ctaType} CTA`, 'button');
    };

    const handleElementClick = (elementName: string) => {
        trackClick(`element-${elementName}`, `Element - ${elementName}`, 'section');
    };

    const elements = [
        {
            id: 'monitor',
            number: 1,
            icon: Eye,
            title: 'Monitor',
            tagline: "What's happening right now?",
            color: 'blue',
            description: `${CONTACT_DETAILS.company.name} connects to your existing systems - whether PLC/SCADA in manufacturing, EHR in healthcare, or IoT sensors in agriculture - to analyze data continuously in real-time. You get visibility into your operations without replacing what already works.`,
            benefits: [
                'Connect to existing data sources and systems',
                'Real-time operational visibility across your infrastructure',
                'Automatic data collection and normalization',
                'Baseline establishment for predictive models'
            ],
            industryExamples: [
                { industry: 'Manufacturing', example: 'Equipment performance, quality metrics, production data' },
                { industry: 'Healthcare', example: 'Patient vitals, treatment outcomes, resource utilization' },
                { industry: 'Energy', example: 'Load patterns, generation output, grid performance' },
            ]
        },
        {
            id: 'predict',
            number: 2,
            icon: Brain,
            title: 'Predict',
            tagline: "What's likely to happen next?",
            color: 'green',
            description: `Using AI algorithms, ${CONTACT_DETAILS.company.name} identifies patterns in your operational data that indicate potential issues or opportunities before they materialize. Get 2-6 hours (or days) of advance warning to act strategically instead of reactively.`,
            benefits: [
                'Early warning alerts before issues occur',
                'Pattern recognition across historical data',
                'Probability-based forecasting for key events',
                'Trend analysis for strategic planning'
            ],
            industryExamples: [
                { industry: 'Manufacturing', example: 'Equipment failures, quality issues, supply disruptions' },
                { industry: 'Healthcare', example: 'Patient deterioration, readmission risks, demand spikes' },
                { industry: 'Energy', example: 'Demand spikes, renewable output fluctuations, maintenance needs' },
            ]
        },
        {
            id: 'optimize',
            number: 3,
            icon: Target,
            title: 'Optimize',
            tagline: 'What should you do about it?',
            color: 'purple',
            description: `${CONTACT_DETAILS.company.name} doesn't just predict - it provides specific guidance and recommendations for resource allocation, timing, and strategic actions. Turn predictions into actionable intelligence that improves outcomes and efficiency.`,
            benefits: [
                'Specific recommendations, not just alerts',
                'Resource allocation optimization',
                'Timing guidance for maximum impact',
                'ROI-focused action plans'
            ],
            industryExamples: [
                { industry: 'Manufacturing', example: 'Production scheduling, inventory levels, maintenance timing' },
                { industry: 'Healthcare', example: 'Staffing levels, bed allocation, treatment protocols' },
                { industry: 'Energy', example: 'Generation dispatch, storage utilization, grid balancing' },
            ]
        },
        {
            id: 'preserve',
            number: 4,
            icon: Archive,
            title: 'Preserve',
            tagline: 'How do you capture this intelligence?',
            color: 'orange',
            description: `When ${CONTACT_DETAILS.company.name} detects an issue or opportunity, it doesn't just alert you - it provides immediate, context-specific guidance pulled from your manuals, procedures, and expert knowledge. No hunting through PDFs or calling senior staff. Workers get the right solution at the right moment, while the system continuously learns and preserves expertise that would otherwise walk out the door.`,
            benefits: [
                'Instant access to relevant procedures and solutions',
                'AI links alerts directly to actionable guidance',
                'No more hunting through manuals or documentation',
                'Capture and preserve expertise before people leave'
            ],
            industryExamples: [
                { industry: 'Manufacturing', example: 'Troubleshooting steps, quality procedures, equipment-specific fixes' },
                { industry: 'Healthcare', example: 'Treatment protocols, intervention steps, care pathways linked to alerts' },
                { industry: 'Energy', example: 'Safety procedures, system responses, optimization protocols' },
            ]
        }
    ];

    const colorClasses = {
        blue: 'text-blue-600 bg-blue-100 border-blue-200',
        purple: 'text-purple-600 bg-purple-100 border-purple-200',
        green: 'text-green-600 bg-green-100 border-green-200',
        orange: 'text-orange-600 bg-orange-100 border-orange-200'
    };

    return (
        <>
            <Helmet>
                <title>Four-Element Methodology | Monitor, Predict, Optimize, Preserve | {CONTACT_DETAILS.company.name}</title>
                <meta name="description" content={`${CONTACT_DETAILS.company.name} applies the four-element methodology - Monitor, Predict, Optimize, Preserve - across industries to transform reactive operations into predictive intelligence.`} />
                <meta name="keywords" content="predictive analytics, four elements, monitor predict optimize preserve, cross-industry AI, predictive intelligence" />
                <link rel="canonical" href="https://4castlabs.com/methodology" />
                <meta property="og:title" content={`Four-Element Methodology | ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content="Monitor, Predict, Optimize, Preserve - the proven four-element methodology for cross-industry predictive intelligence." />
                <meta property="og:url" content="https://4castlabs.com/methodology" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.png" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [{
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": "https://4castlabs.com/"
                        }, {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "Methodology",
                            "item": "https://4castlabs.com/methodology"
                        }]
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Hero Section */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            The {CONTACT_DETAILS.company.name} Methodology
                        </h1>
                        <p className="text-2xl text-gray-700 mb-4 font-semibold">
                            Four elements. Infinite applications.
                        </p>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                            Whether you're in healthcare, manufacturing, energy, or forestry - the
                            same
                            four-element approach transforms reactive operations into predictive intelligence.
                        </p>

                        <div className="flex flex-wrap justify-center gap-4 mb-12">
                            <div
                                className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm border-2 border-blue-200">
                                <div
                                    className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold mr-2">1
                                </div>
                                <span className="text-sm font-medium">Monitor</span>
                            </div>
                            <div
                                className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm border-2 border-green-200">
                                <div
                                    className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold mr-2">2
                                </div>
                                <span className="text-sm font-medium">Predict</span>
                            </div>
                            <div
                                className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm border-2 border-purple-200">
                                <div
                                    className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold mr-2">3
                                </div>
                                <span className="text-sm font-medium">Optimize</span>
                            </div>
                            <div
                                className="flex items-center bg-white px-4 py-2 rounded-full shadow-sm border-2 border-orange-200">
                                <div
                                    className="w-6 h-6 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold mr-2">4
                                </div>
                                <span className="text-sm font-medium">Preserve</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap justify-center gap-4 mb-12">
                            <div className="flex items-center space-x-2 mb-4">
                                <img
                                    src='/images/4-elements.webp'
                                    alt={`${CONTACT_DETAILS.company.name} Logo`}
                                    className="h-80 w-80 object-contain"
                                    loading="lazy"
                                />
                            </div>
                        </div>

                            {/* Pilot Partner Callout */}
                            <div
                                className="bg-gradient-to-r from-primary-50 to-blue-50 border-l-4 border-primary-500 p-6 rounded-r-lg max-w-4xl mx-auto">
                                <div className="flex items-start">
                                    <Lightbulb className="h-6 w-6 text-primary-600 mr-3 flex-shrink-0 mt-1"/>
                                    <div className="text-left">
                                        <p className="text-lg font-semibold text-primary-900 mb-1">
                                            Seeking Pilot Partners Across Industries
                                        </p>
                                        <p className="text-primary-800">
                                            {CONTACT_DETAILS.company.name} is looking for forward-thinking organizations
                                            in healthcare, energy, forestry, and manufacturing for
                                            collaborative pilot implementations. Implement all four elements on one critical
                                            use case with direct developer access and flexible arrangements.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Four Elements Sections */}
                        <div className="space-y-16 mb-16">
                            {elements.map((element) => {
                                const Icon = element.icon;
                                const borderColor = element.color === 'blue' ? '#2563eb' : element.color === 'purple' ? '#9333ea' : element.color === 'green' ? '#16a34a' : '#ea580c';
                                const iconColor = element.color === 'blue' ? 'text-blue-600' : element.color === 'purple' ? 'text-purple-600' : element.color === 'green' ? 'text-green-600' : 'text-orange-600';
                                const checkColor = element.color === 'blue' ? 'text-blue-500' : element.color === 'purple' ? 'text-purple-500' : element.color === 'green' ? 'text-green-500' : 'text-orange-500';
                                const bgColor = element.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : element.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : element.color === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700';
                                const borderColor2 = element.color === 'blue' ? 'border-blue-600 text-blue-600 hover:bg-blue-600' : element.color === 'purple' ? 'border-purple-600 text-purple-600 hover:bg-purple-600' : element.color === 'green' ? 'border-green-600 text-green-600 hover:bg-green-600' : 'border-orange-600 text-orange-600 hover:bg-orange-600';
                                const borderColor3 = element.color === 'blue' ? 'border-blue-200' : element.color === 'purple' ? 'border-purple-200' : element.color === 'green' ? 'border-green-200' : 'border-orange-200';

                                return (
                                    <section key={element.id} id={element.id} className="scroll-mt-20">
                                        <Card
                                            className="p-8 hover:shadow-xl transition-shadow cursor-pointer bg-white border-l-4"
                                            style={{borderLeftColor: borderColor}}
                                            onClick={() => handleElementClick(element.title)}
                                        >
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                                {/* Element Info */}
                                                <div className="lg:col-span-2">
                                                    <div className="flex items-start mb-6">
                                                        <div
                                                            className={`w-16 h-16 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${colorClasses[element.color as keyof typeof colorClasses]}`}>
                                                            <span className="text-2xl font-bold">{element.number}</span>
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center mb-2">
                                                                <Icon className={`h-8 w-8 mr-3 ${iconColor}`}/>
                                                                <h2 className="text-3xl font-bold text-gray-900">{element.title}</h2>
                                                            </div>
                                                            <p className="text-xl text-gray-600 italic">{element.tagline}</p>
                                                        </div>
                                                    </div>

                                                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                                        {element.description}
                                                    </p>

                                                    <div className="mb-6">
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Key
                                                            Capabilities:</h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {element.benefits.map((benefit, idx) => (
                                                                <div key={idx} className="flex items-start">
                                                                    <CheckCircle
                                                                        className={`h-5 w-5 mr-2 mt-0.5 flex-shrink-0 ${checkColor}`}/>
                                                                    <span className="text-gray-700">{benefit}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Industry Examples */}
                                                    <div
                                                        className={`border-2 rounded-lg p-4 ${colorClasses[element.color as keyof typeof colorClasses]}`}>
                                                        <h3 className="font-semibold text-gray-900 mb-3">Industry
                                                            Applications:</h3>
                                                        <div className="space-y-2">
                                                            {element.industryExamples.map((example, idx) => (
                                                                <div key={idx} className="text-sm">
                                                                    <span
                                                                        className="font-semibold text-gray-800">{example.industry}:</span>{' '}
                                                                    <span
                                                                        className="text-gray-700">{example.example}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* CTA Box */}
                                                <div className="lg:col-span-1">
                                                    <div
                                                        className={`border-2 rounded-xl p-6 shadow-sm bg-white ${borderColor3}`}>
                                                        <div
                                                            className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${colorClasses[element.color as keyof typeof colorClasses]}`}>
                                                            <span className="text-xl font-bold">{element.number}</span>
                                                        </div>
                                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                                            Start with {element.title}
                                                        </h3>
                                                        <p className="text-gray-700 mb-4 text-sm">
                                                            See how the {element.title} element applies to your industry
                                                            and operational challenges.
                                                        </p>

                                                        <div className="space-y-2">
                                                            <Link
                                                                to="/assessment"
                                                                className={`block w-full text-white text-center py-2 px-4 rounded-lg transition-colors ${bgColor}`}
                                                                onClick={() => handleCTAClick(`${element.title}-assessment`)}
                                                            >
                                                                Take Assessment
                                                            </Link>
                                                            <Link
                                                                to={`/contact?source=methodology-${element.id}`}
                                                                className={`block w-full border-2 text-center py-2 px-4 rounded-lg transition-colors ${borderColor2} hover:text-white`}
                                                                onClick={() => handleCTAClick(`${element.title}-contact`)}
                                                            >
                                                                Discuss Pilot
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </section>
                                );
                            })}
                        </div>

                        {/* How It Works Section */}
                        <section className="mb-16 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg">
                            <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
                                How {CONTACT_DETAILS.company.name} Works
                            </h2>
                            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
                                The four-element approach applies universally, but implementation is customized to your
                                industry and specific challenges.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                {[
                                    {
                                        step: '1',
                                        title: 'Assess & Identify',
                                        description: 'Evaluate your operations and identify the best starting point for four-element implementation',
                                        icon: Users
                                    },
                                    {
                                        step: '2',
                                        title: 'Connect & Monitor',
                                        description: 'Integrate with existing systems and establish baseline monitoring across your data sources',
                                        icon: Eye
                                    },
                                    {
                                        step: '3',
                                        title: 'Predict & Optimize',
                                        description: 'Deploy AI models for prediction and receive actionable optimization recommendations',
                                        icon: Brain
                                    },
                                    {
                                        step: '4',
                                        title: 'Preserve & Scale',
                                        description: 'Capture expert knowledge and expand the four elements to additional operations',
                                        icon: Zap
                                    }
                                ].map((phase, index) => {
                                    const PhaseIcon = phase.icon;
                                    return (
                                        <div key={index} className="text-center bg-white rounded-xl p-6 shadow-sm">
                                            <div
                                                className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                                                {phase.step}
                                            </div>
                                            <PhaseIcon className="h-8 w-8 text-primary-600 mx-auto mb-3"/>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{phase.title}</h3>
                                            <p className="text-gray-600 text-sm">{phase.description}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* FAQ Section */}
                        <section className="mb-16">
                            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked
                                Questions</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {[
                                    {
                                        q: "Does this work for my industry?",
                                        a: "Yes. The four-element methodology (Monitor, Predict, Optimize, Preserve) applies universally across healthcare, manufacturing, energy, forestry, and beyond. The fundamental questions are the same - only the specific data sources and applications differ."
                                    },
                                    {
                                        q: "How do you customize the four elements for different industries?",
                                        a: `${CONTACT_DETAILS.company.name} uses the same core methodology but adapts data sources, prediction models, and optimization recommendations to industry-specific challenges. For example, Monitor connects to EHRs in healthcare but PLCs in manufacturing - different systems, same element.`
                                    },
                                    {
                                        q: "Do you work with older or specialized systems?",
                                        a: `Yes. Older and specialized systems across any industry often benefit most from the four elements. ${CONTACT_DETAILS.company.name} works with any system that has basic data monitoring capabilities - age and specialization are not barriers.`
                                    },
                                    {
                                        q: "How does the Preserve element actually work?",
                                        a: "When the system detects an issue or opportunity, it doesn't just alert you - it uses AI (RAG technology) to instantly retrieve relevant procedures, solutions, and expert guidance from your documentation. Workers get the right answer at the right moment, no hunting through manuals."
                                    },
                                    {
                                        q: "Can the four elements work with our existing systems?",
                                        a: `Absolutely. ${CONTACT_DETAILS.company.name} integrates with industry-standard systems across sectors - PLC/SCADA, EHR, IoT platforms, booking systems, and more. The approach enhances what you have rather than replacing it.`
                                    },
                                    {
                                        q: "How do you validate predictions across different industries?",
                                        a: "Each industry has unique validation metrics. In manufacturing, we validate against actual equipment failures. In healthcare, against patient outcomes. In agriculture, against yield results. The methodology is consistent; the validation is industry-specific."
                                    }
                                ].map((faq, index) => (
                                    <Card key={index} className="p-6 bg-white hover:shadow-lg transition-shadow">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                                        <p className="text-gray-700">{faq.a}</p>
                                    </Card>
                                ))}
                            </div>
                        </section>

                        {/* CTA Section */}
                        <section
                            className="text-center bg-gradient-to-r from-primary-600 to-blue-700 text-white rounded-2xl p-12 shadow-xl">
                            <h2 className="text-3xl font-bold mb-6">
                                Ready to Experience the <span className="text-4xl">4</span>-Element Approach?
                            </h2>
                            <p className="text-xl mb-4 text-blue-100">
                                Start with a free assessment to discover how Monitor, Predict, Optimize, and Preserve
                                apply to your specific industry and operational challenges.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                                <Link
                                    to="/assessment"
                                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white text-primary-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
                                    onClick={() => handleCTAClick('final-assessment')}
                                >
                                    Take Free Assessment
                                    <ArrowRight className="ml-2 h-5 w-5"/>
                                </Link>
                                <Link
                                    to="/contact?source=methodology-four-elements"
                                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-900 transition-colors"
                                    onClick={() => handleCTAClick('final-contact')}
                                >
                                    Discuss Pilot Program
                                    <ArrowRight className="ml-2 h-5 w-5"/>
                                </Link>
                            </div>
                        </section>
                    </div>
                </div>
            </>
    );
};